import React, { useEffect, useState, useRef } from 'react'
import {
  BlockingLoader,
  Button,
  Container,
  HtmlContent,
  Icon,
  State,
  TextBody,
  ToastContext
} from '@pmi/pmi-iqos-ds-react'
import {
  Assets,
  analytics,
  constants,
  cookie,
  updateCartCounter,
  getToastMessagesFromResponse,
  getToastMessagesFromError,
  extractErrorCode,
  formatProductUrl,
  mapBundleListForDisplay,
  mapProductListForDisplay,
  fillProductsWithExtraData,
  JWTDecode,
  isAuthenticated,
  checkStockNotification,
  isPurchaseLimitationsErrorCode,
  isCartPurchaseLimitationsErrorCode,
  formattedDescriptionJSONLD,
  htmlTranslation,
  resetGrecaptcha,
  dispatchPopupPromotionAlreadyUsed,
  goTo,
  getRoute,
  BazaarVoice,
  Routes,
  Sites
} from '@pmi/pmi-iqos-shared-react'
import {
  ModalNotifyMe,
  ProductDetailContent,
  ProductListCrossSell,
  ProductListUpSell
} from '@zyn-common/components'
import { DcxApiManager, ZynAnalytics } from '@zyn-common/services'

const ANALYTICS_LIST_NAME = 'Recommended (PDP)'
const ANALYTICS_LIST_NAME_UPSELL = 'Upsell'
const ERR_NOT_AVAILABLE_ZIP = '124'

const eligibilityMonitorCansErrorCode = {
  NOT_ELIGIBLE_ERROR: 're_01'
}

const JSONLD = ({ product, option, canonicalUrl }) => {
  if (option) {
    const optionAttributes = {
      name: option.variantName,
      description: formattedDescriptionJSONLD(option.description),
      url: getRoute(
        formatProductUrl(product, option),
        true,
        false,
        null,
        null,
        Sites.zyn
      ),
      image: option.images && option.images.length && option.images[0].path,
      offers: {
        price: option.price,
        availability: option.inStock
          ? Assets.buildSchemaUrl(Assets.schemaSections.inStock)
          : Assets.buildSchemaUrl(Assets.schemaSections.outOfStock),
        url: getRoute(
          formatProductUrl(product, option),
          true,
          false,
          null,
          null,
          Sites.zyn
        ),
        sku: Number(option.productCode)
      }
    }
    const searchProduct = {
      ...optionAttributes,
      '@context': Assets.buildSchemaUrl(),
      '@type': 'Product',
      '@id': canonicalUrl,
      brand: {
        '@type': 'Thing',
        name: 'IQOS'
      },
      offers: {
        ...optionAttributes.offers,
        '@type': 'Offer',
        priceCurrency: 'JPY',
        itemCondition: Assets.buildSchemaUrl(Assets.schemaSections.newCondition)
      }
    }
    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(searchProduct, null, ' ')
        }}
      />
    )
  }
  return null
}

const ProductDetailView = ({
  translations,
  productData,
  productExtraDataLoaded,
  crossSellBanner,
  showBar,
  hideReviews,
  hideRelatedProducts,
  hideProduct,
  backgroundColor,
  updateDataProduct,
  updateData,
  bundleDetailPath,
  canonicalUrl,
  isMulti = false,
  line,
  isMonitorPack = false,
  selectedItem
}) => {
  const [bazaarVoiceReady, setBazaarVoiceReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingButton, setLoadingButton] = useState(false)
  const [loadingRelatedProducts, setLoadingRelatedProducts] = useState(false)
  const [extraDataLoaded, setExtraDataLoaded] = useState(
    productExtraDataLoaded || false
  )
  const [shortestDeliveryDate, setShortestDeliveryDate] = useState('')
  const [crossSell, setCrossSell] = useState([])
  const [upSell, setUpSell] = useState([])
  const [toastColorVisible, setToastColorVisible] = useState(false)
  const [modalNotifyMe, setModalNotifyMe] = useState({
    key: 0,
    isOpen: false,
    product: null,
    selectedOptionIndex: 0,
    isAuthenticated: false,
    email: undefined,
    name: undefined
  })
  const [show3dModelBtn, setShow3dModelBtn] = useState(false)

  const updateSelectedOptionIndexRef = useRef(false)
  const product = productData.product
  const quantity = productData.quantity
  const selectedOptionIndex = productData.selectedOptionIndex
  const options = productData.product.options
  const toastContext = ToastContext.getContextActions()
  const popupToast = toastContext.popupToast
  const dismissToast = toastContext.dismissToast
  const isZyn = true

  const api = new DcxApiManager(line)

  const listenLoginSuccessEvent = (kill = false) => {
    const e = constants.REACT_LOGIN_SUCCESS_EVENT
    const f = loadData
    if (kill) {
      window.removeEventListener(e, f)
    } else {
      window.addEventListener(e, f)
    }
  }

  const listenBazaarVoiceReviewInitEvent = (kill = false) => {
    const e = 'bazaarVoiceReviewInit'
    const f = setCurrentBazaarVoiceReview
    if (kill) {
      window.removeEventListener(e, f)
    } else {
      window.addEventListener(e, f)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    listenLoginSuccessEvent()
    listenBazaarVoiceReviewInitEvent()
    return () => {
      listenLoginSuccessEvent(true)
      listenBazaarVoiceReviewInitEvent(true)
    }
  }, [listenLoginSuccessEvent, listenBazaarVoiceReviewInitEvent])

  useEffect(() => {
    if (bazaarVoiceReady) {
      setCurrentBazaarVoiceReview()
    }
  }, [bazaarVoiceReady])

  const setCurrentBazaarVoiceReview = (
    newSelectedOptionIndex = selectedOptionIndex
  ) => {
    const selectedOption = options && options[newSelectedOptionIndex]
    const bazaarVoice = product && product.bv
    const bazaarVoiceProductId = selectedOption
      ? selectedOption.bv_id
      : bazaarVoice
      ? bazaarVoice.bv_id
      : null
    const bazaarVoiceShowRating =
      !hideProduct &&
      bazaarVoice &&
      bazaarVoice.display_rating === true &&
      bazaarVoiceReady
    if (bazaarVoiceShowRating) {
      window.dispatchEvent(
        new window.CustomEvent('bazaarVoiceProductIdChanged', {
          detail: bazaarVoiceProductId
        })
      )
    }
  }

  const loadData = async () => {
    setProductsLoading()
    if (!productExtraDataLoaded) {
      analytics.productDetailsView(
        { product: product, options: options },
        selectedOptionIndex,
        Sites.zyn
      )
      getProductsExtraData()
      analyticsProductListImpression()
    } else {
      setBazaarVoiceReady(true)
    }
    setShow3dModelBtn(product.model3D)
    await getRelatedProducts()
    checkProductsLoading()
  }

  const setProductsLoading = () => {
    if (ProductDetailView.productsLoading) {
      ProductDetailView.productsLoading[product.id] = true
    } else {
      ProductDetailView.productsLoading = {
        [product.id]: true
      }
    }
  }

  const checkProductsLoading = () => {
    // Event to let Drupal know when can scroll the page
    if (ProductDetailView.productsLoading) {
      ProductDetailView.productsLoading[product.id] = false
    }
    const pdpElems = document.querySelectorAll(
      '.react-component-ProductDetailMulti, .react-component-ProductDetail'
    )
    const productsLoadingIds = Object.keys(
      ProductDetailView.productsLoading || {}
    )
    if (pdpElems.length === productsLoadingIds.length) {
      let stillLoading = false
      productsLoadingIds.forEach(p => {
        if (ProductDetailView.productsLoading[p]) stillLoading = true
      })
      if (!stillLoading) {
        setTimeout(() => {
          window.dispatchEvent(new window.CustomEvent('productFullyLoaded'))
        }, 100)
      }
    }
  }

  const handleExtraDataLoaded = updatedProduct => {
    updateDataProduct({
      ...updatedProduct,
      productExtraDataLoaded: true
    })
  }

  const handleProductQuantityChange = quantity => {
    updateData({
      quantity
    })
  }

  const handleProductSelectedOptionIndexChange = (
    newSelectedOptionIndex = selectedOptionIndex
  ) => {
    updateData({
      selectedOptionIndex: newSelectedOptionIndex
    })
  }

  const handleOptionsUpdate = (updatedProduct, options) => {
    updateDataProduct({
      ...updatedProduct,
      options: [...options]
    })
  }

  const getProductsExtraData = async () => {
    const productid = product && product.id
    const variantid =
      options &&
      selectedOptionIndex === 0 &&
      options[selectedOptionIndex].variantId
    const params = {
      stock: true,
      promo: true,
      productid,
      variantid,
      isMonitorPack
    }
    let res = null
    setExtraDataLoaded(false)
    setLoading(true)
    try {
      res = await api.cart.getProducts({ params })
    } catch (err) {
      if (err.data && err.data.data && err.data.data.products) {
        res = err.data.data
      }
    }
    if (res) {
      const updatedProducts = fillProductsWithExtraData(
        [{ ...product, options }],
        res.products
      )
      const productSelected =
        updatedProducts.find(productData => productData.id === selectedItem) ||
        updatedProducts[0]
      updatedProducts &&
        updatedProducts.length > 0 &&
        handleExtraDataLoaded(productSelected)
      getShortestDeliveryDate(productSelected.options[selectedOptionIndex])
      dispatchPopupPromotionAlreadyUsed(
        cookie.getSpiceId(),
        updatedProducts,
        popupToast,
        translations,
        Container,
        TextBody
      )
    }
    setLoading(false)
    setExtraDataLoaded(true)
    setBazaarVoiceReady(true)
  }

  const getRelatedProducts = async (
    newSelectedOptionIndex = selectedOptionIndex
  ) => {
    if (hideRelatedProducts) {
      return
    }
    const selectedOption =
      options &&
      (newSelectedOptionIndex || newSelectedOptionIndex === 0) &&
      options[newSelectedOptionIndex]
    const productid = product && product.id
    const variantid = selectedOption && selectedOption.variantId
    try {
      setLoadingRelatedProducts(true)
      setCrossSell([])
      setUpSell([])
      const res = await api.cart.getRelatedProducts({
        params: {
          productid,
          variantid
        }
      })
      setCrossSell(
        res.cross_sell ? mapProductListForDisplay(res.cross_sell, false) : []
      )
      setUpSell(res.up_sell ? mapBundleListForDisplay(res.up_sell, false) : [])
    } catch (err) {
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_error,
        subtitle: selectedOption && selectedOption.variantName,
        content: (
          <TextBody
            size={TextBody.sizes.s}
            acceptsHtml
            value={getToastMessagesFromError(err)}
          />
        )
      })
    } finally {
      setLoadingRelatedProducts(false)
    }
  }

  const getShortestDeliveryDate = async selectedOption => {
    if (selectedOption && selectedOption.inStock) {
      if (isAuthenticated()) {
        try {
          if (!product.preorderable) {
            const res = await api.cart.shortestDelivery([
              { code: selectedOption.productCode, qty: 1 }
            ])
            setShortestDeliveryDate(
              res.day && res.day.leadDays && res.timeSlots && res.timeSlots[0]
                ? res.timeSlots[0].label
                : ''
            )
          } else {
            setShortestDeliveryDate('')
          }
        } catch (err) {
          if (err.data && err.data[0].code === ERR_NOT_AVAILABLE_ZIP) {
            setShortestDeliveryDate(translations.tk_defaultShortestDateValue)
          } else {
            setShortestDeliveryDate('')
          }
        }
      } else {
        setShortestDeliveryDate(translations.tk_defaultShortestDateValue)
      }
    } else {
      setShortestDeliveryDate('')
    }
  }

  const analyticsProductListImpression = () => {
    if (crossSell && crossSell.length) {
      analytics.productListImpression(crossSell, ANALYTICS_LIST_NAME)
    }
    if (upSell && upSell.length) {
      analytics.productListImpression(upSell, ANALYTICS_LIST_NAME)
    }
  }

  const handleColorChange = async newSelectedOptionIndex => {
    updateSelectedOptionIndexRef.current = true
    const optionChanged = async () => {
      if (updateSelectedOptionIndexRef.current) {
        updateSelectedOptionIndexRef.current = false
        handleProductQuantityChange(1)
        const selectedOption = options[newSelectedOptionIndex]
        window.dispatchEvent(
          new window.CustomEvent('productVariantChanged', {
            detail: formatProductUrl(product, selectedOption)
          })
        )
        analytics.productDetailsView(
          { product: product, options: options },
          newSelectedOptionIndex,
          Sites.zyn
        )
        dispatchPopupPromotionAlreadyUsed(
          cookie.getSpiceId(),
          [
            {
              options: options,

              selectedOptionIndex: newSelectedOptionIndex
            }
          ],
          popupToast,
          translations,
          Container,
          TextBody
        )
        await getRelatedProducts(newSelectedOptionIndex)
        setCurrentBazaarVoiceReview(newSelectedOptionIndex)
        getShortestDeliveryDate(selectedOption)
      }
    }
    optionChanged()
    handleProductSelectedOptionIndexChange(newSelectedOptionIndex)
  }

  const handleIntensityChange = async newSelectedOptionIndex => {
    updateSelectedOptionIndexRef.current = true
    const optionChanged = async () => {
      if (updateSelectedOptionIndexRef.current) {
        updateSelectedOptionIndexRef.current = false
        handleProductQuantityChange(1)
        const selectedOption = options[newSelectedOptionIndex]
        window.dispatchEvent(
          new window.CustomEvent('productVariantChanged', {
            detail: formatProductUrl(product, selectedOption)
          })
        )
        analytics.productDetailsView(
          { product: product, options: options },
          newSelectedOptionIndex,
          Sites.zyn
        )
        dispatchPopupPromotionAlreadyUsed(
          cookie.getSpiceId(),
          [
            {
              options: options,

              selectedOptionIndex: newSelectedOptionIndex
            }
          ],
          popupToast,
          translations,
          Container,
          TextBody
        )
        getShortestDeliveryDate(selectedOption)
      }
    }
    optionChanged()
    handleProductSelectedOptionIndexChange(newSelectedOptionIndex)
  }

  const handleQuantityChange = newQuantity => {
    handleProductQuantityChange(newQuantity)
  }

  const cartFreeShippingToastContent = msg => {
    return (
      <Container display={Container.displays.flex} pb="2">
        <Container pr="2">
          <Icon size={Icon.sizes.s} image={Icon.images.orders} />
        </Container>
        <TextBody size={TextBody.sizes.s} acceptsHtml value={msg} />
      </Container>
    )
  }

  const handleEligibilityErrorToast = () => {
    ZynAnalytics.addToCartErrorPopupClick(
      translations.tk_eligibilityErrorMonitorCansTitle,
      translations.tk_eligibilityErrorMonitorCansCTA
    )
    dismissToast()
  }

  const handleAddToCart = async (productToAdd, selectedOptionIndexToAdd) => {
    const token = cookie.getToken()
    const decodedToken = JWTDecode(token)
    if (
      decodedToken &&
      decodedToken.has_device_registered === 'false' &&
      productToAdd.isRequiredDeviceRegistration
    ) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_productNeedsRegisteredDevices
      })
      return false
    }

    const isCrossSell = !(
      productToAdd.id === product.id &&
      selectedOptionIndexToAdd === selectedOptionIndex
    )
    const selectedOption = productToAdd.options[selectedOptionIndexToAdd]

    !isCrossSell && setLoading(true)

    try {
      setLoadingButton(true)
      const res = await api.cart.addToCart(
        [
          {
            id: productToAdd.id,
            qty: isCrossSell ? 1 : quantity,
            option: selectedOption
          }
        ],
        null,
        null,
        !!(productToAdd.isMonitorPack || isMonitorPack)
      )

      updateCartCounter(res.amount)

      const showFamilyWarning = res.familyNotice && !cookie.getFamilyWarning()
      if (showFamilyWarning) {
        cookie.setFamilyWarning(true)
        !isCrossSell && analytics.productFamilyConflict(false)
      }

      const resProducts = await api.cart.getProducts({
        params: {
          productid: productToAdd.id,
          variantid: selectedOption.variantId,
          promo: true,
          isMonitorPack
        }
      })
      const productSelected =
        resProducts.products.find(
          productData => productData.id === selectedItem
        ) || resProducts.products[0]
      handleOptionsUpdate(
        productSelected,
        productSelected.options.map((option, index) => ({
          ...options[index],
          optionId: Number(option.option_id),
          variantId: Number(option.variant_id),
          price: parseFloat(option.price),
          listPrice: parseFloat(option.list_price),
          bv_id: (option.bv && option.bv.bv_id) || null
        }))
      )
      await getRelatedProducts()

      if (res.isFirstOrder && res.amount < 2) {
        popupToast({
          title: translations.tk_eligibilityWarningMonitorCansTitle,
          subtitle: translations.tk_eligibilityWarningMonitorCansDescription,
          actions: [
            <Button
              key={0}
              fullWidth
              variant={Button.variants.secondary}
              text={translations.tk_eligibilityWarningMonitorCansCTA}
              onClickHandler={() => dismissToast()}
            />
          ]
        })
      } else {
        popupToast({
          disclaimer: res.promotionDisclaimer,
          type: res.error ? ToastContext.types.error : ToastContext.types.none,
          title: res.error
            ? translations.tk_productAddedError
            : translations.tk_productAddedOk,
          subtitle: selectedOption && selectedOption.variantName,
          actions:
            res.error || isCrossSell
              ? null
              : [
                  <Button
                    key={0}
                    variant={Button.variants.secondary}
                    text={translations.tk_toastKeepShopping}
                    onClickHandler={() => handleKeepShopping(showFamilyWarning)}
                    data-testing="ProductDetail_Button_KeepShopping"
                  />,
                  <Button
                    key={1}
                    variant={Button.variants.primary}
                    text={translations.tk_toastProceedToCart}
                    onClickHandler={() => handleGoToCart(showFamilyWarning)}
                    data-testing="ProductDetail_Button_GoToCart"
                  />
                ],
          content: (
            <Container>
              {res.free_shipping_message &&
                cartFreeShippingToastContent(res.free_shipping_message)}
              {showFamilyWarning && (
                <Container
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.row}
                  alignItems={Container.alignItemsValues.center}
                  pb="4">
                  <Container pr="4">
                    <State
                      size={State.sizes.s}
                      state={State.states.warningRed}
                    />
                  </Container>
                  <TextBody
                    style={TextBody.styles.error}
                    {...htmlTranslation(
                      translations,
                      'tk_addedToCartFamilyNotice'
                    )}
                  />
                </Container>
              )}
              <TextBody>{getToastMessagesFromResponse(res)}</TextBody>
            </Container>
          )
        })
      }

      // for crossSell analytics is done in product-card
      if (!isCrossSell && !res.error) {
        analytics.addToCart(
          { ...productToAdd, options: productToAdd.options },
          selectedOptionIndex,
          quantity,
          Sites.zyn
        )
      }
    } catch (err) {
      const errorCode = extractErrorCode(err)
      const isPLError = isPurchaseLimitationsErrorCode(errorCode)
      const isMonitorFlow = !!(productToAdd.isMonitorPack || isMonitorPack)
      if (
        isMonitorFlow &&
        errorCode === eligibilityMonitorCansErrorCode.NOT_ELIGIBLE_ERROR
      ) {
        ZynAnalytics.addToCartErrorPopupDisplay(
          translations.tk_eligibilityErrorMonitorCansTitle
        )
        popupToast({
          title: translations.tk_eligibilityErrorMonitorCansTitle,
          subtitle: translations.tk_eligibilityErrorMonitorCansDescription,
          type: ToastContext.types.error,
          actions: [
            <Button
              key={0}
              fullWidth
              variant={Button.variants.secondary}
              text={translations.tk_eligibilityErrorMonitorCansCTA}
              onClickHandler={() => handleEligibilityErrorToast()}
            />
          ],
          onClose: () => {
            handleEligibilityErrorToast()
          }
        })
      } else {
        if (isPLError) {
          analytics.addToCartLimitations(
            selectedOption && selectedOption.variantId,
            selectedOption && selectedOption.variantName,
            isCartPurchaseLimitationsErrorCode(errorCode)
          )
        }
        popupToast({
          type: ToastContext.types.error,
          title: translations.tk_productAddedError,
          subtitle: selectedOption && selectedOption.variantName,
          content: (
            <Container>
              <TextBody
                size={TextBody.sizes.s}
                acceptsHtml
                value={getToastMessagesFromError(err)}
              />
              {isPLError && (
                <TextBody
                  size={TextBody.sizes.s}
                  {...htmlTranslation(
                    translations,
                    'tk_purchaseLimitationsErrorTermsAndConditions'
                  )}
                />
              )}
            </Container>
          )
        })
      }
    } finally {
      setLoadingButton(false)
    }

    if (!isCrossSell) {
      setLoading(false)
      setToastColorVisible(false)
    }
  }

  const handleAddToPreorder = async (product, selectedOptionIndex) => {
    if (isAuthenticated()) {
      try {
        setLoadingButton(true)
        const profile = await api.profile.getProfile()
        const stockNotification = await api.cart.getStockNotification(product)
        const alreadySubscribed = checkStockNotification(
          product,
          selectedOptionIndex,
          stockNotification
        )
        if (alreadySubscribed) {
          popupToast({
            subtitle: translations.tk_notifyMeAlreadySubscribedTitle
          })
        } else {
          if (line) {
            await api.cart.addStockNotification(
              null,
              product.id,
              product.options[selectedOptionIndex],
              null
            )
          }
          setModalNotifyMe({
            key: Math.random(),
            product: product,
            selectedOptionIndex: selectedOptionIndex,
            isAuthenticated: true,
            email: profile.EmailAddress,
            name: profile.LastName + ' ' + profile.FirstName,
            isOpen: true
          })
        }
      } catch (err) {
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_addedToPreorderError
        })
      } finally {
        setLoadingButton(false)
      }
    } else {
      setModalNotifyMe({
        key: Math.random(),
        product: product,
        selectedOptionIndex: selectedOptionIndex,
        isAuthenticated: false,
        email: undefined,
        name: undefined,
        isOpen: true
      })
    }
  }

  const handleGoToCart = familyWarning => {
    familyWarning && analytics.productFamilyConflictDecision(true)
    goTo(Routes.cart, true, false, null, null, Sites.zyn)
  }

  const handleKeepShopping = familyWarning => {
    familyWarning && analytics.productFamilyConflictDecision(false)
    dismissToast()
  }

  const handleLoginToSeeMore = () => {
    goTo(
      Routes.userLogin,
      false,
      false,
      [`callredirectUrl=${formatProductUrl(product)}`],
      null,
      Sites.zyn
    )
  }

  const handleOnNotifyMe = async (
    userInfo,
    product,
    selectedOptionIndex,
    recaptchaData
  ) => {
    const selectedOption = product.options[selectedOptionIndex]
    try {
      await api.cart.addStockNotification(
        userInfo,
        product.id,
        selectedOption,
        recaptchaData
      )
      return {
        success: true
      }
    } catch (err) {
      const code =
        err.data &&
        Array.isArray(err.data) &&
        err.data.length > 0 &&
        err.data[0].code
      if (
        code === constants.CAPTCHA_VALIDATION_ERROR_01 ||
        code === constants.CAPTCHA_VALIDATION_ERROR_02
      ) {
        return {
          success: false,
          captchaError: true
        }
      } else {
        resetGrecaptcha(constants.STOCK_NOTIFICATION_GRECAPTCHA_ACTION)
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_addedToPreorderError
        })
        return {
          success: false,
          captchaError: false,
          error:
            err.data &&
            Array.isArray(err.data) &&
            err.data.length > 0 &&
            err.data[0].message
        }
      }
    }
  }

  const handleOnUpSellBundleClick = (upSellBundle, index) => {
    const url = bundleDetailPath ? bundleDetailPath + upSellBundle.id : ''
    analytics.productClickBundle(
      upSellBundle,
      index,
      ANALYTICS_LIST_NAME_UPSELL,
      url
    )
    url && goTo(url)
  }

  const selectedOption = options && options[selectedOptionIndex]
  const bazaarVoice = product && product.bv
  const bazaarVoiceProductId = selectedOption
    ? selectedOption.bv_id
    : bazaarVoice
    ? bazaarVoice.bv_id
    : null
  const bazaarVoiceShowRating =
    bazaarVoice &&
    bazaarVoice.display_rating === true &&
    bazaarVoiceProductId &&
    bazaarVoiceReady

  return (
    <>
      {bazaarVoiceShowRating && <BazaarVoice.OverrideStyle />}
      <>
        {product && (
          <ProductDetailContent
            product={product}
            options={options}
            selectedOptionIndex={selectedOptionIndex}
            quantity={quantity}
            loading={loading}
            loadingButton={loadingButton}
            extraDataLoaded={extraDataLoaded}
            showBar={showBar}
            bazaarVoiceReady={bazaarVoiceReady}
            shortestDeliveryDate={shortestDeliveryDate}
            addToCartHandler={async e => {
              e.preventDefault()
              handleAddToCart(
                { ...product, options: options },
                selectedOptionIndex
              )
            }}
            addToPreorderHandler={async e => {
              e.preventDefault()
              handleAddToPreorder(
                { ...product, options: options },
                selectedOptionIndex
              )
            }}
            colorChangeHandler={handleColorChange}
            intensityChangeHandler={handleIntensityChange}
            loginToSeeMoreHandler={handleLoginToSeeMore}
            toastColorVisible={toastColorVisible}
            onOpenColorToast={e => {
              e.preventDefault()
              setToastColorVisible(true)
            }}
            onCloseColorToast={() => {
              setToastColorVisible(false)
            }}
            translations={translations}
            backgroundColor={backgroundColor}
            onQuantityChange={handleQuantityChange}
            show3dModelBtn={show3dModelBtn}
            isMulti={isMulti}
            line={line}
          />
        )}
        {crossSellBanner && (
          <HtmlContent
            className="cross-sell-banner-container"
            fullWidth
            content={crossSellBanner}
          />
        )}
        {!isZyn &&
          !hideRelatedProducts &&
          (loadingRelatedProducts ? (
            <Container fullWidth pt="12">
              <Container
                fullWidth
                p="10"
                position={Container.positions.relative}>
                <BlockingLoader loading />
              </Container>
            </Container>
          ) : (
            <Container fullWidth pt={['0', '5', '0']}>
              {!line && crossSell && crossSell.length > 0 && (
                <ProductListCrossSell
                  products={crossSell}
                  translations={translations}
                  onAddToCart={handleAddToCart}
                  onAddToPreorder={handleAddToPreorder}
                />
              )}
              {!line && upSell && upSell.length > 0 && (
                <ProductListUpSell
                  products={upSell}
                  translations={translations}
                  onAddToCart={handleOnUpSellBundleClick}
                  buttonAddToCartLabel={translations.tk_selectAnOption}
                  bundleDetailPath={bundleDetailPath}
                />
              )}
            </Container>
          ))}
        {!isZyn && bazaarVoiceShowRating && !hideReviews && (
          <Container pt="12">
            <BazaarVoice
              type={BazaarVoice.types.reviews}
              productId={bazaarVoiceProductId}
              data-testing-prefix="ProductDetail"
            />
          </Container>
        )}
        <ModalNotifyMe
          key={modalNotifyMe.key}
          isAuthenticated={modalNotifyMe.isAuthenticated}
          defaultEmail={modalNotifyMe.email}
          defaultName={modalNotifyMe.name}
          translations={translations}
          isOpen={modalNotifyMe.isOpen}
          product={modalNotifyMe.product}
          selectedOptionIndex={modalNotifyMe.selectedOptionIndex}
          handleOnNotifyMe={handleOnNotifyMe}
          line={line}
          onClose={() => {
            setModalNotifyMe({
              ...modalNotifyMe,
              ...{ isOpen: false }
            })
          }}
        />
        <JSONLD
          product={product}
          option={options && options[selectedOptionIndex]}
          canonicalUrl={canonicalUrl}
        />
      </>
    </>
  )
}

export default ProductDetailView
